import { useRedirectLogOutUser } from "../../../customHook/useRedirectLogOutUser";
import { useMemo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTable, usePagination, useGlobalFilter, useFilters } from "react-table";
import { GlobalFilter } from "../../globalFilter";
import { getCumulativeProductBatches } from "../../../redux/features/product/productBatchSlice";
import ReactToPrint from "react-to-print";
import { SelectColumnFilter, multiSelectFilter } from "./SelectColumnFilter1";
import { EthiopianDateCell } from '../EthiopianDateCell';


const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

export const CumulativeProducts = () => {
  useRedirectLogOutUser();

  const dispatch  = useDispatch();
  const printRef  = useRef();
  const [printView, setPrintView] = useState(false);

  useEffect(() => {
    dispatch(getCumulativeProductBatches());
  }, [dispatch]);

  const ProductsCumulative = useSelector((state) => state.productBatch.cumulativeBatches || []);
  const { userType }       = useSelector((state) => state.auth);
  const isAdmin            = userType === "admin";

  // ── Columns ───────────────────────────────────────────────────────────────
  // ✅ printView removed from deps — it does NOT change column definitions,
  //    only the Filter render in JSX is conditionally hidden.
  //    Keeping it in deps caused the "Maximum update depth exceeded" loop.
  const columns = useMemo(() => {
    const base = [
      {
        Header: "No",
        id: "no",
        Cell: ({ row }) => row.index + 1,
        disableFilters: true,
      },
      {
        Header: "Product Name",
        accessor: "product.name",
        Filter: SelectColumnFilter,
        filter: "multiSelectFilter",
        Cell: ({ value }) => dash(value),
      },
      {
        Header: "SKU",
        accessor: "product.sku",
        Filter: SelectColumnFilter,
        filter: "multiSelectFilter",
        Cell: ({ value }) => dash(value),
      },
      {
        Header: "Total Remaining Qty",
        accessor: "totalRemainingQuantity",
        Filter: SelectColumnFilter,
        filter: "multiSelectFilter",
        Cell: ({ value }) => value != null ? value.toLocaleString() : "—",
      },
      {
        Header: "Latest Selling Price",
        accessor: "latestSellingPrice",
        disableFilters: true,
        Cell: ({ value }) => value != null ? `${value.toLocaleString()} birr` : "—",
      },
      {
        Header: "Avg Selling Price",
        accessor: "avgSellingPrice",
        disableFilters: true,
        Cell: ({ value }) => value != null
          ? `${Math.round(value).toLocaleString()} birr`
          : "—",
      },
      {
        Header: "Current Selling Value",
        accessor: "currentSellingValue",
        disableFilters: true,
        Cell: ({ value }) => value != null
          ? <span className="text-success fw-semibold">{Math.round(value).toLocaleString()} birr</span>
          : "—",
      },
      {
        Header: "Last Batch Date",
        accessor: "lastBatchDate",
        Filter: SelectColumnFilter,
        filter: "multiSelectFilter",
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"

      },
    ];

    // Admin-only columns (purchase price & cost value)
    if (isAdmin) {
      base.splice(4, 0,
        {
          Header: "Latest Purchase Price",
          accessor: "latestPurchasePrice",
          disableFilters: true,
          Cell: ({ value }) => value != null ? `${value.toLocaleString()} birr` : "—",
        },
        {
          Header: "Avg Purchase Price",
          accessor: "avgPurchasePrice",
          disableFilters: true,
          Cell: ({ value }) => value != null
            ? `${Math.round(value).toLocaleString()} birr`
            : "—",
        },
        {
          Header: "Current Purchase Value",
          accessor: "currentPurchaseValue",
          disableFilters: true,
          Cell: ({ value }) => value != null
            ? <span className="text-primary fw-semibold">{Math.round(value).toLocaleString()} birr</span>
            : "—",
        }
      );
    }

    return base;
  // ✅ only isAdmin — NOT printView
  }, [isAdmin]);

  const data        = useMemo(() => Array.isArray(ProductsCumulative) ? ProductsCumulative : [], [ProductsCumulative]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable(
    { columns, data, filterTypes, defaultColumn: { Filter: () => null }, 
    autoResetPage: false,       // ← prevents page reset on data change
    autoResetFilters: false,    // ← prevents filter reset on data change
    autoResetGlobalFilter: false, // ← prevents global filter reset
 },
    useFilters, useGlobalFilter, usePagination
  );

  const {
    getTableProps, getTableBodyProps, headerGroups,
    page, rows,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, pageOptions,
    setGlobalFilter, setPageSize,
    state: { pageIndex, globalFilter, pageSize },
    prepareRow,
  } = tableInstance;

  // ── Subtotals (all filtered rows, not just current page) ─────────────────
  const subtotals = useMemo(() => ({
    totalRemainingQty:    rows.reduce((s, r) => s + (r.original.totalRemainingQuantity || 0), 0),
    currentSellingValue:  rows.reduce((s, r) => s + (r.original.currentSellingValue   || 0), 0),
    currentPurchaseValue: rows.reduce((s, r) => s + (r.original.currentPurchaseValue  || 0), 0),
  }), [rows]);

  return (
    <div>

      {/* ── Header bar ──────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <h4 className="mb-0">Cumulative Products</h4>
        <ReactToPrint
          trigger={() => (
            <button className="btn btn-primary rounded-3" onClick={() => setPrintView(true)}>
              Print Table
            </button>
          )}
          content={() => printRef.current}
          onAfterPrint={() => setPrintView(false)}
          onBeforeGetContent={async () => setPrintView(true)}
          pageStyle="@page { size: A4 landscape; margin: 10mm; }"
        />
      </div>

      {/* ── Search ──────────────────────────────────────────────────────── */}
      <div className="px-4 mb-3">
        <div className="px-4 rounded-4 border" style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>
      </div>

      {/* ── Summary cards ───────────────────────────────────────────────── */}
      <div className="px-4 mb-3" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "1rem",
      }}>
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>TOTAL REMAINING QTY</p>
          <p className="fw-bold fs-5 mb-0">{subtotals.totalRemainingQty.toLocaleString()}</p>
        </div>
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>CURRENT SELLING VALUE</p>
          <p className="fw-bold fs-5 mb-0 text-success">
            {Math.round(subtotals.currentSellingValue).toLocaleString()} birr
          </p>
        </div>
        {isAdmin && (
          <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
            <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>CURRENT PURCHASE VALUE</p>
            <p className="fw-bold fs-5 mb-0 text-primary">
              {Math.round(subtotals.currentPurchaseValue).toLocaleString()} birr
            </p>
          </div>
        )}
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>DISTINCT PRODUCTS</p>
          <p className="fw-bold fs-5 mb-0">{data.length}</p>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="px-4" ref={printRef}>
        {printView && (
          <>
            <div style={{ padding: "1.5rem" }}><h2>Cumulative Products Table</h2></div>
            <div style={{ marginBottom: "1rem", textAlign: "right", fontSize: "1.5rem" }}>
              Date: {new Date().toLocaleDateString()}
            </div>
          </>
        )}

        <div className="table-responsive rounded-4 border" style={{ backgroundColor: "#fff" }}>
          <table className="table table-striped table-hover caption-top mb-0"
            {...getTableProps()} style={{ fontSize: "12px", minWidth: "100%" }}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th className="px-3" style={{ backgroundColor: "#faf8f5" }}
                      {...column.getHeaderProps()}>
                      {column.render("Header")}
                      {/* ✅ Filter hidden in print — no state change inside render */}
                      {!printView && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="px-3" {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {page.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    No cumulative product data found.
                  </td>
                </tr>
              )}
            </tbody>

            {/* ── Subtotal footer ────────────────────────────────────────── */}
            <tfoot>
              {isAdmin ? (
                <tr style={{ backgroundColor: "#e8f4ff", fontWeight: 700 }}>
                  <td className="px-3 py-2" colSpan={4}>
                    Subtotal ({rows.length} products)
                  </td>
                  <td className="px-3 py-2">{subtotals.totalRemainingQty.toLocaleString()}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-primary">
                    {Math.round(subtotals.currentPurchaseValue).toLocaleString()} birr
                  </td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-success">
                    {Math.round(subtotals.currentSellingValue).toLocaleString()} birr
                  </td>
                  <td className="px-3 py-2" colSpan={2}></td>
                </tr>
              ) : (
                <tr style={{ backgroundColor: "#e8f4ff", fontWeight: 700 }}>
                  <td className="px-3 py-2" colSpan={4}>
                    Subtotal ({rows.length} products)
                  </td>
                  <td className="px-3 py-2">{subtotals.totalRemainingQty.toLocaleString()}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-success">
                    {Math.round(subtotals.currentSellingValue).toLocaleString()} birr
                  </td>
                  <td className="px-3 py-2" colSpan={2}></td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: "15px" }}>
            page <strong><em style={{ color: "blue" }}>{pageIndex + 1}</em> of {pageOptions.length}</strong>
          </span>
          <select className="form-select form-select-lg" style={{ maxWidth: "90px" }}
            value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 15, 20, 30, 40, 50, data.length].map((s, i) => (
              <option key={i} value={s}>Show {s}</option>
            ))}
          </select>
          <li><button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button></li>
          <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
            <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
            <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li><button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button></li>
        </ul>
      </div>

    </div>
  );
};